import React, { createContext, useContext, useMemo } from 'react'

const MembersContext = createContext()

/**
 *
 * @returns {{
 * userIsAdmin: boolean
 * allMembers: import('@fs/zion-tree-types').GroupMember[]
 * admins: import('@fs/zion-tree-types').GroupMember[]
 * nonAdmins: import('@fs/zion-tree-types').GroupMember[]
 * pendingMembers: import('@fs/zion-tree-types').GroupMember[]
 * currentMemberDetails: import('@fs/zion-tree-types').GroupMember | undefined
 * refetchGroupMembers: import('@fs/zion-tree-types/zion-axios').RefetchFunction
 * }}
 */

export const useMembersContext = () => {
  const context = useContext(MembersContext)
  if (!context) throw new Error('You need to use the MembersProvider to use useMembersContext')
  return context
}

/**
 *
 * @param {{
 * children: import('react').ReactNode
 * allMembers: import('@fs/zion-tree-types').GroupMember[]
 * pendingMembers: import('@fs/zion-tree-types').GroupMember[]
 * refetchGroupMembers: import('@fs/zion-tree-types/zion-axios').RefetchFunction
 * currentMemberDetails: import('@fs/zion-tree-types').GroupMember | undefined
 * userIsDataAdmin: boolean
 * }} props
 * @returns
 */

export default function MembersProvider({
  children,
  allMembers,
  pendingMembers,
  refetchGroupMembers,
  currentMemberDetails,
  userIsDataAdmin,
}) {
  const { admins, nonAdmins } = useMemo(
    () =>
      allMembers.reduce(
        (accum, member) => {
          if (member.status === 'ADMIN') accum.admins.push(member)
          else accum.nonAdmins.push(member)
          return accum
        },
        { admins: [], nonAdmins: [] }
      ),
    [allMembers]
  )
  const userIsAdmin = currentMemberDetails?.status === 'ADMIN' || userIsDataAdmin
  const value = useMemo(
    () => ({
      userIsAdmin,
      allMembers,
      admins,
      nonAdmins,
      pendingMembers,
      currentMemberDetails,
      refetchGroupMembers,
    }),
    [admins, allMembers, currentMemberDetails, userIsAdmin, nonAdmins, pendingMembers, refetchGroupMembers]
  )
  return <MembersContext.Provider value={value}>{children}</MembersContext.Provider>
}
